/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect
} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJobTasksElem} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {onRenderAppIdOption, onRenderInput} from './helpers';
import {useJobTaskChooserStyles} from './useJobTaskChooserStyles';

// defines the app ID chooser prop definitions ...
export type JobTaskAppIdChooserProps = {
  row: DownloadJobTasksElem;
  onChange: (event: ChangeEvent, value: string) => void;
}

/**
 * The JobTaskAppIdChooser provides a list of app IDs for a job's selected
 * devices and device/file type, allowing the list of device file names for
 * the job to be filtered.
 *
 * ```typescript
 *
 *     import {JobTaskAppIdChooser} from './JobTaskAppIdChooser';
 *
 *     // with callback subscription ...
 *     <JobTaskAppIdChooser onChange={handleAppId(value, index, tasks)} />
 *
 * ```
 */
export const JobTaskAppIdChooser = ({
  row,
  onChange
}: JobTaskAppIdChooserProps) => {
  const {task}              = row;
  const {dropdownProps}     = useJobTaskChooserStyles();
  const onGetSelectedOption = useCallback((option, value) => option === value, []);
  const onGetOptionLabel    = useCallback(option => option || '', []);
  const enabled             = task?.usesAppIds;
  const appIds              = task?.deviceAppIds?.length ? task?.deviceAppIds : task?.fileAppIds;

  useEffect(() => {
    if (appIds?.length === 1) {
      onChange(null, appIds[0]);
    }
  }, [safeJSONStringify(appIds)]);

  return (
    <Autocomplete disableClearable={!appIds?.length || appIds?.length === 1}
      disabled={!enabled}
      options={(enabled && appIds) || []}
      value={(enabled && task?.appId) || ''}
      getOptionSelected={onGetSelectedOption}
      filterOptions={filterOptions}
      classes={dropdownProps}
      getOptionLabel={onGetOptionLabel}
      renderOption={onRenderAppIdOption}
      onChange={onChange}
      renderInput={onRenderInput(translate('jobcreate:fields.appId.placeholder'))} />
  );
};

/**
 * Filters options by comparing the current dropdown value with each option. If an option
 * contains the value, it is included in the returned list.
 */
const filterOptions = (options: string[], {inputValue}: {inputValue: string}) => {
  if (inputValue === '') {
    return options;
  } else {
    const values = [];
    const value  = inputValue.toLowerCase();
    options.forEach(option => {
      if (option.includes(value)) {
        values.push(option);
      }
    });
    return values;
  }
};

export default JobTaskAppIdChooser;
